"use client"

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Input } from './ui/input'
import { Search, Plus, Tag, Edit, Trash2 } from 'lucide-react'
import apiClient from '../lib/api-client'
import { API_BASE_URL } from '../config'
import { sortMatchesByDate } from '../utils/playerSorting'

export default function MatchesTab({ setActiveTab, setSelectedMatchId }) {
  const [matches, setMatches] = useState([])
  const [matchesWithImages, setMatchesWithImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddMatchForm, setShowAddMatchForm] = useState(false)
  const [editingMatch, setEditingMatch] = useState(null)
  const [error, setError] = useState(null)
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    opponent: '',
    competition: '',
    venue: '',
    result: '',
    notes: ''
  })

  // Helper function to construct S3 URL
  const constructS3Url = useCallback((key) => {
    if (!key) return null;
    const cleanKey = key.startsWith('/') ? key.substring(1) : key;
    return `https://sponsor-detection-images.s3.eu-west-1.amazonaws.com/${encodeURIComponent(cleanKey)}`;
  }, []);

  // Helper function to get image URL
  const getImageUrl = useCallback((image) => {
    if (!image) return null;
    if (image.url) return image.url;
    if (image.imageUrl) return image.imageUrl;
    if (image.s3Url) return image.s3Url;
    if (image.s3Key) return constructS3Url(image.s3Key);
    if (image.metadata && image.metadata.s3Key) return constructS3Url(image.metadata.s3Key);
    if (image.id && (image.id.includes('/') || image.id.startsWith('batch/'))) {
      return constructS3Url(image.id);
    }
    return null;
  }, [constructS3Url]);

  // Fetch matches with image data
  const fetchMatchesWithImages = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/matches-with-images`);

      
      // Process the matches data from the API
      const processedMatches = response.data.map(match => {
        // Get images from the match data
        const images = match.images || [];
        const imageCount = images.length;

        
        // Get the first image URL if available
        let imageUrl = null;
        if (images.length > 0) {
          const firstImage = images[0];
          imageUrl = getImageUrl(firstImage);
        }
        
        return {
          ...match,
          imageCount,
          imageUrl,
          firstImageId: images.length > 0 ? images[0].id : null
        };
      }); // Include ALL matches, not just those with images
      
      setMatchesWithImages(processedMatches);
    } catch (error) {
      console.error('Error fetching matches-with-images, using fallback:', error);
      try {
        // Fallback: fetch matches and images separately
        const imagesResponse = await apiClient.get(`${API_BASE_URL}/images`);
        let allImages = [];
        if (Array.isArray(imagesResponse.data)) allImages = imagesResponse.data;
        else if (imagesResponse.data?.images) allImages = imagesResponse.data.images;
        else if (imagesResponse.data?.data) allImages = imagesResponse.data.data;

        const matchesWithImagesData = matches
          .map(match => {
            const matchImages = allImages.filter(img => img.matchId === match.id);
            return {
              ...match,
              imageCount: matchImages.length,
              imageUrl: matchImages.length > 0 ? getImageUrl(matchImages[0]) : null,
              firstImageId: matchImages.length > 0 ? matchImages[0].id : null
            };
          }); // Include ALL matches, not just those with images
        setMatchesWithImages(matchesWithImagesData);
      } catch (fallbackError) {
        console.error('Error in fetchMatchesWithImages fallback:', fallbackError);
        setMatchesWithImages(matches.map(m => ({ ...m, imageCount: 0, imageUrl: null })));
      }
    }
  }, [matches, getImageUrl]);
  
  // Fetch matches from API
  const fetchMatches = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(`${API_BASE_URL}/matches`, {
        params: { searchTerm }
      })
      
      // Check if response has the expected structure with success and data properties
      let matchesData = []
      if (Array.isArray(response.data)) {
        matchesData = response.data
      } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        matchesData = response.data.data
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        matchesData = [response.data]
      }
      
      // Store matches in localStorage as a fallback
      try {
        localStorage.setItem('matches', JSON.stringify(matchesData))
      } catch (storageError) {
        console.error('Error storing matches in localStorage:', storageError)
      }
      
      setMatches(matchesData)
    } catch (error) {
      console.error('Error fetching matches:', error)
      setError('Failed to load matches. Please try again.')
      
      // Try to load from localStorage as fallback
      try {
        const storedMatches = localStorage.getItem('matches')
        if (storedMatches) {
          const parsedMatches = JSON.parse(storedMatches)
          setMatches(parsedMatches)
        }
      } catch (storageError) {
        console.error('Error loading matches from localStorage:', storageError)
      }
    } finally {
      setLoading(false)
    }
  }, [searchTerm])
  
  // Fetch matches on component mount and when search term changes
  useEffect(() => {
    fetchMatches()
  }, [fetchMatches])

  // Fetch matches with images when matches are loaded
  useEffect(() => {
    if (matches.length > 0) {
      fetchMatchesWithImages();
    }
  }, [matches, fetchMatchesWithImages])
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }
  
  // Reset form data
  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      opponent: '',
      competition: '',
      venue: '',
      result: '',
      notes: ''
    })
    setEditingMatch(null)
  }
  
  // Show add match form
  const handleAddMatch = () => {
    resetForm()
    setShowAddMatchForm(true)
  }
  
  // Show edit match form
  const handleEditMatch = (match) => {
    setFormData({
      title: match.title || '',
      date: match.date || '',
      opponent: match.opponent || '',
      competition: match.competition || '',
      venue: match.venue || '',
      result: match.result || '',
      notes: match.notes || ''
    })
    setEditingMatch(match.id)
    setShowAddMatchForm(true)
  }
  
  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      if (editingMatch) {
        // Update existing match
        await apiClient.put(`${API_BASE_URL}/matches/${editingMatch}`, formData)
        alert('Match updated successfully')
      } else {
        // Create new match
        await apiClient.post(`${API_BASE_URL}/matches`, formData)
        alert('Match created successfully')
      }
      
      // Reset form and refresh matches
      setShowAddMatchForm(false)
      resetForm()
      fetchMatches()
    } catch (error) {
      console.error('Error saving match:', error)
      alert('Failed to save match. Please try again.')
    }
  }
  
  // Delete match
  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Are you sure you want to delete this match?')) {
      return
    }
    
    try {
      console.log('Deleting match:', matchId)
      const response = await apiClient.delete(`${API_BASE_URL}/matches/${matchId}`)
      
      // Check if response has data and success property
      if (response.data && response.data.success) {
        console.log('Delete response:', response.data)
        alert('Match deleted successfully')
        fetchMatches()
      } else {
        // Handle unexpected success response format
        console.error('Unexpected response format:', response)
        alert('Match deleted, but the response format was unexpected. Refreshing matches list.')
        fetchMatches()
      }
    } catch (error) {
      console.error('Error deleting match:', error)
      
      // Extract the error message from the response if available
      const errorMessage = error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : 'Failed to delete match. Please try again.'
      
      alert(errorMessage)
    }
  }
  
  // Handle viewing images for a match
  const handleViewImages = (matchId) => {
    try {
      // Set the selected match ID in the parent component
      if (setSelectedMatchId) {
        setSelectedMatchId(matchId);
      }
      
      // Navigate to the library tab
      if (setActiveTab) {
        setActiveTab('library');
      }
    } catch (error) {
      console.error('Error navigating to library tab:', error);
      alert('Failed to navigate to the Library tab. Please try again.');
    }
  }

  // Memoized sorted matches to prevent sorting on every render
  const sortedMatches = useMemo(() => {
    return sortMatchesByDate(matches);
  }, [matches]);
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Matches</h2>
        <Button onClick={handleAddMatch}>
          <Plus className="mr-2 h-4 w-4" /> Add Match
        </Button>
      </div>
      
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
        <Input
          placeholder="Search matches..."
          className="pl-8"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {error && (
        <div className="bg-red-50 text-red-500 p-3 rounded-md">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading matches...</p>
        </div>
      ) : !Array.isArray(matches) || matches.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-lg">
          <p className="text-gray-500">
            {searchTerm 
              ? "No matches found for your search. Try a different search term."
              : "No matches found. Add your first match to get started."}
          </p>
        </div>
      ) : (
        <div>
          <h3 className="font-medium mb-4">All Matches</h3>
          
          {/* List view for all matches */}
          <div className="space-y-2">
            {sortedMatches.map(match => {
              // Find image count for this match
              const matchWithImages = matchesWithImages.find(m => m.id === match.id);
              const imageCount = matchWithImages ? (matchWithImages.imageCount || 0) : 0;
              
              return (
                <Card key={match.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium">{match.title}</h4>
                        <p className="text-sm text-gray-500">
                          {match.date ? new Date(match.date).toLocaleDateString() : 'No date'}
                          {match.opponent && ` • vs ${match.opponent}`}
                          {` • ${imageCount} ${imageCount === 1 ? 'image' : 'images'}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleViewImages(match.id)}>
                          <Tag className="mr-2 h-4 w-4" /> View Images ({imageCount})
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEditMatch(match)}>
                          <Edit className="mr-2 h-4 w-4" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteMatch(match.id)}>
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Add/Edit Match Form */}
      {showAddMatchForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-[600px] w-full max-h-[90vh] overflow-auto">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">{editingMatch ? 'Edit Match' : 'Add New Match'}</h2>
                <button onClick={() => setShowAddMatchForm(false)}>×</button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium mb-1">
                  Match Title*
                </label>
                <input
                  id="title"
                  name="title"
                  className="w-full h-10 px-3 py-2 rounded-md border border-input"
                  placeholder="e.g. Shamrock Rovers vs Bohemians"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div>
                <label htmlFor="date" className="block text-sm font-medium mb-1">
                  Date*
                </label>
                <input
                  id="date"
                  name="date"
                  type="date"
                  className="w-full h-10 px-3 py-2 rounded-md border border-input"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="opponent" className="block text-sm font-medium mb-1">
                    Opponent*
                  </label>
                  <input
                    id="opponent"
                    name="opponent"
                    className="w-full h-10 px-3 py-2 rounded-md border border-input"
                    placeholder="e.g. Bohemians"
                    value={formData.opponent}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="competition" className="block text-sm font-medium mb-1">
                    Competition
                  </label>
                  <input
                    id="competition"
                    name="competition"
                    className="w-full h-10 px-3 py-2 rounded-md border border-input"
                    placeholder="e.g. League of Ireland"
                    value={formData.competition}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="venue" className="block text-sm font-medium mb-1">
                    Venue
                  </label>
                  <input
                    id="venue"
                    name="venue"
                    className="w-full h-10 px-3 py-2 rounded-md border border-input"
                    placeholder="e.g. Tallaght Stadium"
                    value={formData.venue}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <label htmlFor="result" className="block text-sm font-medium mb-1">
                    Result
                  </label>
                  <input
                    id="result"
                    name="result"
                    className="w-full h-10 px-3 py-2 rounded-md border border-input"
                    placeholder="e.g. 1-0"
                    value={formData.result}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="notes" className="block text-sm font-medium mb-1">
                  Notes
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  className="w-full min-h-[100px] px-3 py-2 rounded-md border border-input"
                  placeholder="Any additional notes about the match..."
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                  onClick={() => setShowAddMatchForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                >
                  {editingMatch ? 'Update Match' : 'Create Match'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
